import { useState } from "react";
import { Check } from "lucide-react";

import { api, ApiError, type SettingsResponse } from "@/api/client";
import { Button } from "@/components/ui/button";

const INPUT = "field w-32 tabular-nums";

/** Mirrors TwoTrailProperties.MAX_TRAIL_TO_SL_RATIO (the server's PARAM_WHIPSAW_BOUND). */
const MAX_TRAIL_TO_SL_RATIO = 0.15;

/** Editable numeric params, grouped. {@code def} is the compiled fallback; the input placeholder shows
 *  the running (ConfigMap-bound) value from /api/settings when available (see {@link EFFECTIVE}). */
const SECTIONS: { title: string; fields: { key: string; label: string; def: string }[] }[] = [
  {
    title: "Mécanique two-trail",
    fields: [
      { key: "slPoints", label: "SL (pts)", def: "1000" },
      { key: "reinforceThresholdPoints", label: "Renfort r (pts)", def: "75" },
      { key: "trailDistancePoints", label: "Trail r_trail (pts)", def: "75" },
      { key: "maxSide", label: "Max positions (côté biais)", def: "3" },
      // Schema 14 — seuil d'armement du trail découplé du plafond (0 = suit maxSide).
      { key: "trailArmCount", label: "Armement trail dès (0 = maxSide)", def: "0" },
      { key: "trailStairAdvancePoints", label: "Escalier trail — avance post-armement (0 = off)", def: "0" },
      { key: "trailStairTrailPoints", label: "Escalier trail — rt resserré (0 = off, doit être < rt)", def: "0" },
    ],
  },
  {
    title: "Chaîne AUTO (pauses & reprises)",
    fields: [
      { key: "autoLossThresholdPips", label: "Seuil de perte (pts/position)", def: "5" },
      { key: "autoConsecutiveLossLimit", label: "Pertes consécutives avant pause", def: "2" },
      { key: "autoResumePullbackPts", label: "Pull-back pertes (pts, 0 = réouverture)", def: "0" },
      { key: "autoWinPullbackPts", label: "Attente-gain WINPB (pts, 0 = off)", def: "0" },
      { key: "autoNoOpenBeforeCloseMinutes", label: "Fenêtre sans-ouverture (min)", def: "60" },
    ],
  },
  {
    title: "Budget risque du cycle (%)",
    fields: [
      { key: "cycleRiskPct", label: "Risque cycle (fraction, DEMO+LIVE)", def: "0.20" },
    ],
  },
  {
    title: "Caps de positions & flux",
    fields: [
      { key: "maxTotalHard", label: "Cap dur (max total)", def: "6" },
      { key: "consecutiveStopLimit", label: "Limite stops consécutifs", def: "3" },
      { key: "maxSpreadPoints", label: "Spread max (pts)", def: "7" },
    ],
  },
  {
    title: "Financement & politique marché fermé",
    fields: [
      { key: "financingBaseRatePct", label: "Taux (€STR, fraction)", def: "0.0193" },
      // V20 — minutes before the next calendar close at which the proactive flatten fires
      { key: "flattenLeadMinutes", label: "Avance du flatten (min)", def: "15" },
    ],
  },
];

const INT_KEYS = new Set([
  "maxSide",
  "autoConsecutiveLossLimit",
  "autoNoOpenBeforeCloseMinutes",
  "trailArmCount",
  "trailStairAdvancePoints",
  "trailStairTrailPoints",
  "maxTotalHard",
  "consecutiveStopLimit",
  "maxSpreadPoints",
  "flattenLeadMinutes",
]);

/**
 * Field key → its running (ConfigMap-bound) value from {@code /api/settings} — the value the engine
 * actually executes, so it's what we show as each input's placeholder ("blank = leave unchanged").
 * Keys absent here (e.g. {@code financingBaseRatePct}, not exposed by /api/settings) fall back to the
 * SECTIONS compiled default. NB {@code cycleRiskPct} lives under {@code liveSafety}, not twoTrail.
 */
const EFFECTIVE: Record<string, (s: SettingsResponse) => number | undefined> = {
  slPoints: (s) => s.twoTrail.slPoints,
  reinforceThresholdPoints: (s) => s.twoTrail.reinforceThresholdPoints,
  trailDistancePoints: (s) => s.twoTrail.trailDistancePoints,
  maxSide: (s) => s.twoTrail.maxSide,
  trailArmCount: (s) => s.twoTrail.trailArmCount,
  trailStairAdvancePoints: (s) => s.twoTrail.trailStairAdvancePoints ?? 0,
  trailStairTrailPoints: (s) => s.twoTrail.trailStairTrailPoints ?? 0,
  cycleRiskPct: (s) => s.liveSafety.cycleRiskPct,
  maxTotalHard: (s) => s.positionLimits.maxTotalHard,
  consecutiveStopLimit: (s) => s.positionLimits.consecutiveStopLimit,
  maxSpreadPoints: (s) => s.stream.maxSpreadPoints,
  // schema 16 — knobs AUTO (optional-chained : payload pré-16 possible)
  autoLossThresholdPips: (s) => s.auto?.lossThresholdPips,
  autoConsecutiveLossLimit: (s) => s.auto?.consecutiveLossLimit,
  autoNoOpenBeforeCloseMinutes: (s) => s.auto?.noOpenBeforeCloseMinutes,
  autoResumePullbackPts: (s) => s.auto?.resumePullbackPts,
  autoWinPullbackPts: (s) => s.auto?.winPullbackPts,
  // V20 policy block (optional-chained: an older backend payload may omit it)
  flattenLeadMinutes: (s) => s.policy?.flattenLeadMinutes,
  financingBaseRatePct: (s) => s.policy?.financingBaseRatePct,
};

interface ParamEditFormProps {
  /** Called after a candidate is created so the parent can refresh the versions list. */
  onCreated?: () => void;
  /**
   * The running/effective config (from {@code /api/settings}). Field placeholders show these
   * ConfigMap-bound values (what the engine actually runs) instead of the compiled defaults. Null
   * until loaded → placeholders fall back to the SECTIONS compiled default.
   */
  effective?: SettingsResponse | null;
}

/**
 * Lot 5(8e) — edit the trading+risk parameter set and persist a CANDIDATE. Every input is blank =
 * "leave unchanged"; the server starts from the running config and applies only the fields sent, so you
 * edit a subset. Creating a candidate changes nothing; activate it from the versions list above
 * (OTP + phrase). Client-side guard mirrors the server: r_trail ≤ 0.15·SL. The §19 LIVE cycle-risk cap
 * was removed (owner decision) — cycle-risk-% is editable DEMO + LIVE. Financing rate is the €STR
 * benchmark (operator-set; IG's API doesn't expose it). PROTECTED_LADDER_V6: the no-trade policy is the
 * marketStatus-driven "flat when closed" flag (it replaced the old time-based hold-overnight/hold-weekend).
 */
export default function ParamEditForm({ onCreated, effective }: ParamEditFormProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [financing, setFinancing] = useState<"" | "true" | "false">(""); // "" = unchanged
  const [flatWhenClosed, setFlatWhenClosed] = useState<"" | "true" | "false">(""); // "" = unchanged
  const [openAfterArm, setOpenAfterArm] = useState<"" | "true" | "false">(""); // "" = unchanged (schema 14)
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: number; paramsHash: string } | null>(null);

  const set = (k: string, v: string) => setValues((prev) => ({ ...prev, [k]: v }));

  // The running (ConfigMap-bound) value for a field, or undefined when /api/settings hasn't loaded or
  // doesn't expose it — the input placeholder + the "blank = unchanged" bound checks default to it.
  const effVal = (key: string): number | undefined =>
    effective && EFFECTIVE[key] ? EFFECTIVE[key](effective) : undefined;
  const placeholderOf = (key: string, compiled: string): string => {
    const v = effVal(key);
    return v != null ? String(v) : compiled;
  };

  // effective sl / r_trail for the bound (the entered value, else the running config, else compiled)
  const slEff = Number(values.slPoints?.trim() || effVal("slPoints") || 1000);
  const trailEff = Number(values.trailDistancePoints?.trim() || effVal("trailDistancePoints") || 75);
  const withinBound = slEff > 0 && trailEff > 0 && trailEff <= MAX_TRAIL_TO_SL_RATIO * slEff;

  // Miroir de la validation serveur — le livre est MONO-directionnel depuis no-def
  // (2026-07-16) : le max structurel est maxSide, la seule cohérence à tenir est
  // maxSide ≤ maxTotalHard. (L'ex-check client 2·maxSide ≤ hard était périmé et
  // bloquait des candidates que le serveur accepte.)
  const intEff = (k: string, d: number) => {
    const t = values[k]?.trim();
    return t ? parseInt(t, 10) : (effVal(k) ?? d);
  };
  const side = intEff("maxSide", 3);
  const hard = intEff("maxTotalHard", 6);
  const limitsOk = side >= 1 && side <= hard;
  // Schema 14 — miroir serveur : 0 = suit maxSide ; un seuil explicite doit tenir dans le livre
  // (au-delà de maxSide le trail ne s'armerait jamais → 422 PARAM_LIMITS_INCONSISTENT).
  const armAt = intEff("trailArmCount", 0);
  const armOk = armAt === 0 || (armAt >= 1 && armAt <= side);

  const edited =
    Object.values(values).some((v) => v.trim() !== "") ||
    financing !== "" ||
    flatWhenClosed !== "" ||
    openAfterArm !== "";
  const ready = reason.trim() !== "" && withinBound && limitsOk && armOk && edited && !pending;

  const submit = async () => {
    setPending(true);
    setError(null);
    setCreated(null);
    try {
      const body: Record<string, number | string | boolean> = { reason };
      for (const [k, v] of Object.entries(values)) {
        if (v.trim() === "") continue;
        body[k] = INT_KEYS.has(k) ? parseInt(v, 10) : Number(v);
      }
      if (financing !== "") body.financingEnabled = financing === "true";
      if (flatWhenClosed !== "") body.flatWhenClosed = flatWhenClosed === "true";
      if (openAfterArm !== "") body.openAfterArm = openAfterArm === "true";
      const res = await api.createParameterCandidate(body as Parameters<typeof api.createParameterCandidate>[0]);
      setCreated(res);
      onCreated?.();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 422
          ? "Refusé par une garde serveur (whipsaw r_trail ≤ 0,15·SL)."
          : "Impossible de créer la candidate.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="param-edit-form">
      {SECTIONS.map((section) => (
        <div key={section.title} className="flex flex-col gap-1">
          <span className="text-muted-foreground text-xs font-semibold">{section.title}</span>
          <div className="flex flex-wrap gap-3">
            {section.fields.map((f) => (
              <label key={f.key} className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">{f.label}</span>
                <input
                  type="number"
                  className={INPUT}
                  value={values[f.key] ?? ""}
                  placeholder={placeholderOf(f.key, f.def)}
                  onChange={(e) => set(f.key, e.target.value)}
                  disabled={pending}
                  data-testid={`param-edit-${f.key}`}
                />
              </label>
            ))}
            {section.title.startsWith("Mécanique") && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Renforts sous trail armé</span>
                <select
                  className={INPUT}
                  value={openAfterArm}
                  onChange={(e) => setOpenAfterArm(e.target.value as "" | "true" | "false")}
                  disabled={pending}
                  data-testid="param-edit-openAfterArm"
                >
                  <option value="">(inchangé)</option>
                  <option value="true">continuer jusqu'à maxSide (« +open »)</option>
                  <option value="false">geler le côté armé</option>
                </select>
              </label>
            )}
            {section.title.startsWith("Financement") && (
              <>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-muted-foreground">Financement</span>
                  <select
                    className={INPUT}
                    value={financing}
                    onChange={(e) => setFinancing(e.target.value as "" | "true" | "false")}
                    disabled={pending}
                    data-testid="param-edit-financingEnabled"
                  >
                    <option value="">(inchangé)</option>
                    <option value="true">activé</option>
                    <option value="false">désactivé</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-muted-foreground">Flat marché fermé</span>
                  <select
                    className={INPUT}
                    value={flatWhenClosed}
                    onChange={(e) => setFlatWhenClosed(e.target.value as "" | "true" | "false")}
                    disabled={pending}
                    data-testid="param-edit-flatWhenClosed"
                  >
                    <option value="">(inchangé)</option>
                    <option value="true">flat quand le marché ferme (WE / fériés / pause)</option>
                    <option value="false">tenir marché fermé (secours)</option>
                  </select>
                </label>
              </>
            )}
          </div>
        </div>
      ))}

      {!withinBound && (
        <p className="text-warning text-xs" role="alert" data-testid="param-edit-bound-error">
          r_trail ({trailEff}) doit être ≤ 0,15·SL ({(MAX_TRAIL_TO_SL_RATIO * slEff).toFixed(1)}).
        </p>
      )}
      {!limitsOk && (
        <p className="text-warning text-xs" role="alert" data-testid="param-edit-limits-error">
          Les caps doivent satisfaire maxSide ≤ maxTotalHard (et maxSide ≥ 1) — livre mono-directionnel.
        </p>
      )}
      {!armOk && (
        <p className="text-warning text-xs" role="alert" data-testid="param-edit-arm-error">
          Armement trail : 0 (suit maxSide) ou entre 1 et maxSide ({side}) — au-delà, le trail ne
          s'armerait jamais.
        </p>
      )}

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">Raison</span>
        <input
          type="text"
          className={INPUT + " w-full"}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={pending}
          placeholder="pourquoi ce changement"
          data-testid="param-edit-reason"
        />
      </label>

      <Button type="button" size="sm" onClick={submit} disabled={!ready} data-testid="param-edit-submit">
        {pending ? "Enregistrement…" : "Créer la candidate"}
      </Button>

      {error && (
        <p className="text-loss text-sm" role="alert" data-testid="param-edit-error">
          {error}
        </p>
      )}
      {created && (
        <p
          className="text-foreground flex items-center gap-1 text-sm"
          role="status"
          data-testid="param-edit-result"
        >
          <Check className="text-buy size-3.5" aria-hidden />
          Candidate #{created.id} créée (hash {created.paramsHash.slice(0, 8)}). Active-la depuis la
          liste des versions ci-dessous.
        </p>
      )}
    </div>
  );
}
